import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { Calculator, Lightbulb } from 'lucide-react'
import { renderContent } from '../utils/contentRenderer'

/**
 * Renders a question's alternative/second-method solution as its own labeled, linkable card —
 * separate from the main rationale. Fed by the dedicated `Question.alternative_solution` datapoint
 * (split out of the rationale; see server/lib/schemaV2Presentation.js). Forwards a ref so callers can
 * scroll/anchor to it (e.g. the "Alternative Solution Available" button or a "Jump to solution" link).
 *
 * @param {string} alternativeSolution - the alternative-solution markdown
 * @param {'desmos'|'method'} [type='method'] - drives the icon, label, and accent color
 * @param {string} [contentType='md'] - content type passed to renderContent
 * @param {string} [id] - optional DOM id so the section is anchor-linkable
 */
const AlternativeSolutionSection = forwardRef(function AlternativeSolutionSection(
  { alternativeSolution, type = 'method', contentType = 'md', id },
  ref
) {
  if (!alternativeSolution) return null

  const isDesmos = type === 'desmos'
  const Icon = isDesmos ? Calculator : Lightbulb
  const label = isDesmos ? 'Desmos Alternative' : 'Alternative Solution'

  // Emerald for the Desmos (graphical/calculator) approach, teal for a second analytical method —
  // matching the accent colors used by the alternative-solution buttons on the question surfaces.
  const accent = isDesmos
    ? {
        border: 'border-emerald-200 dark:border-emerald-800',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        heading: 'text-emerald-800 dark:text-emerald-200',
        icon: 'text-emerald-600 dark:text-emerald-400'
      }
    : {
        border: 'border-teal-200 dark:border-teal-800',
        bg: 'bg-teal-50 dark:bg-teal-900/20',
        heading: 'text-teal-800 dark:text-teal-200',
        icon: 'text-teal-600 dark:text-teal-400'
      }

  return (
    <div
      ref={ref}
      id={id}
      className={`rounded-lg border ${accent.border} ${accent.bg} p-4 scroll-mt-4`}
    >
      <h4 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${accent.heading}`}>
        <Icon className={`w-4 h-4 flex-shrink-0 ${accent.icon}`} aria-hidden="true" />
        {label}
      </h4>
      <div className="question-content text-sm text-gray-700 dark:text-gray-300">
        {renderContent(alternativeSolution, contentType)}
      </div>
    </div>
  )
})

AlternativeSolutionSection.propTypes = {
  alternativeSolution: PropTypes.string,
  type: PropTypes.oneOf(['desmos', 'method']),
  contentType: PropTypes.string,
  id: PropTypes.string
}

export default AlternativeSolutionSection
